'use client';

/**
 * Demo-mode context (2026-06-16).
 *
 * One boolean lifted to a React context so every rendering surface
 * (cases list, case page, chat-box page-context badge, TopBar badge,
 * the Cmd+K command branch) reads + writes the same value without
 * prop-drilling.
 *
 * Persistence: `localStorage["cl_demo_mode"]` (boolean serialized as
 * "true" / "false"). On first mount the provider hydrates from there
 * after a paint, mirroring the AI-box-layout-context pattern so the
 * SSR markup stays stable (no flash of wrong state).
 *
 * Behaviour:
 *   - Toggle is global — flipping it on any page affects every other
 *     surface immediately.
 *   - No backend call. The redaction happens at React render time only;
 *     API responses are unchanged.
 *   - Plan §3.1 + §3.7.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'cl_demo_mode';

function loadEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    /* private mode / disabled storage — ignore */
  }
}

interface DemoModeValue {
  /** True iff demo mode is on. */
  enabled: boolean;
  /** Flip the current state. */
  toggle: () => void;
  /** Set to a specific value. */
  setEnabled: (v: boolean) => void;
  /**
   * True iff the provider has hydrated from localStorage. Components that
   * need to avoid an SSR/CSR flash can branch on this — e.g. hide the
   * TopBar badge until hydrated so it doesn't pop in after first paint.
   */
  hydrated: boolean;
}

const Ctx = createContext<DemoModeValue | null>(null);

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setEnabledState(loadEnabled());
    setHydrated(true);
  }, []);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    saveEnabled(v);
  }, []);

  const toggle = useCallback(() => {
    setEnabledState((prev) => {
      const next = !prev;
      saveEnabled(next);
      return next;
    });
  }, []);

  const value = useMemo<DemoModeValue>(
    () => ({ enabled, toggle, setEnabled, hydrated }),
    [enabled, toggle, setEnabled, hydrated],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Read demo-mode state. Outside the provider this returns an inert
 * default (disabled, no-op setters, not hydrated) so unit tests and
 * standalone routes don't have to wrap themselves.
 */
export function useDemoMode(): DemoModeValue {
  const v = useContext(Ctx);
  if (v) return v;
  return {
    enabled: false,
    toggle: () => {},
    setEnabled: () => {},
    hydrated: false,
  };
}
